import { createSelector } from '@reduxjs/toolkit';
import { StatusCodes } from 'http-status-codes';
import { every, find, isEmpty, reduce } from 'lodash';

import { ApiError } from '@sonar/errors';
import { selectStores } from '@sonar/modules/Stores';
import { Status } from '@sonar/types';

import { EntitiesPerStoreState, EntitiesState } from '../Reports';

import { OrderInfo, OrderInfoWithStore, OrderStatus, RootStateWithOrders } from './types';

const selectAllOrdersPerStore = ({
  orders,
}: RootStateWithOrders): EntitiesPerStoreState<OrderInfo> => orders;

export const selectOrdersError = createSelector(
  selectAllOrdersPerStore,
  ordersPerStore => find(ordersPerStore, orders => orders.error !== undefined)?.error,
);

export const selectHasUnprocessableEntityError = createSelector(
  selectOrdersError,
  orderErrors =>
    orderErrors instanceof ApiError && orderErrors.code === StatusCodes.UNPROCESSABLE_ENTITY,
);

export const selectAreOrdersLoaded = createSelector(
  selectAllOrdersPerStore,
  ordersPerStore =>
    isEmpty(ordersPerStore) ||
    every(
      ordersPerStore,
      orders => orders.status === Status.SUCCEEDED || orders.status === Status.FAILED,
    ),
);

export const selectAllOrders = createSelector(
  selectAllOrdersPerStore,
  selectStores,
  (ordersPerStore, stores) =>
    reduce(
      ordersPerStore,
      (orders: OrderInfoWithStore[], { entities }: EntitiesState<OrderInfo>, storeId: string) => {
        const store = stores.find(({ id }) => id === Number(storeId));

        return !store || !entities
          ? orders
          : [...orders, ...entities.map(orderInfo => ({ store, ...orderInfo }))];
      },
      [],
    ),
);

export const selectClosedAndCanceledOrders = createSelector(selectAllOrders, orders =>
  orders.filter(({ orderStatus }) =>
    [OrderStatus.Closed, OrderStatus.Cancelled].includes(orderStatus),
  ),
);
